import { useState } from "react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { X, Download, Mail, FileText, FileSpreadsheet, FileType2, Calendar, Loader2 } from "lucide-react";

// Section keys must match backend `ALL_SECTIONS` in `pdf_pro_report.py`.
const PDF_SECTIONS = [
  { key: "executive_summary",    label: "Executive summary" },
  { key: "ranking_distribution", label: "Ranking distribution" },
  { key: "biggest_wins",         label: "Biggest wins" },
  { key: "biggest_drops",        label: "Biggest drops" },
  { key: "top_opportunities",    label: "Top opportunities" },
  { key: "activity_timeline",    label: "SEO activity timeline" },
  { key: "recommendations",      label: "Recommendations" },
];

const CSV_PRESETS = [
  { key: "today", label: "Today" },
  { key: "7",     label: "Last 7 days" },
  { key: "14",    label: "Last 14 days" },
  { key: "30",    label: "Last 30 days" },
];
const TXT_PRESETS = [
  { key: "7",  label: "Last 7 days" },
  { key: "14", label: "Last 14 days" },
  { key: "30", label: "Last 30 days" },
];
const PDF_PRESETS = [
  { key: "today", label: "Last 24 hours" },
  { key: "7",     label: "Last 7 days" },
  { key: "30",    label: "Last 30 days" },
  { key: "custom", label: "Custom range" },
];

const TABS = [
  { key: "csv", label: "CSV", icon: FileSpreadsheet, hint: "Spreadsheet with per-day rank columns" },
  { key: "pdf", label: "PDF", icon: FileText,        hint: "Client-ready white-label report" },
  { key: "txt", label: "TXT", icon: FileType2,       hint: "Plain-text summary for Slack / email" },
];

function TabButton({ tab, active, onClick }) {
  const Icon = tab.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`export-tab-${tab.key}`}
      className={`flex-1 flex flex-col items-center gap-1.5 px-4 py-3 border-b-2 transition ${
        active
          ? "border-[#0047FF] text-[#0047FF] bg-[#F0F5FF]"
          : "border-transparent text-[#525B6A] hover:text-[#0047FF]"
      }`}
    >
      <Icon className="h-5 w-5" />
      <div className="text-sm font-medium">{tab.label}</div>
      <div className="text-[11px] text-[#8A94A6] text-center leading-tight max-w-[160px]">{tab.hint}</div>
    </button>
  );
}

function RangeChips({ presets, value, onChange, testIdPrefix }) {
  return (
    <div className="flex flex-wrap gap-2">
      {presets.map((p) => (
        <button
          key={p.key}
          type="button"
          onClick={() => onChange(p.key)}
          data-testid={`${testIdPrefix}-range-${p.key}`}
          className={`px-3 py-1.5 rounded-md text-sm border transition ${
            value === p.key
              ? "bg-[#0047FF] border-[#0047FF] text-white"
              : "bg-white border-[#E2E8F0] text-[#525B6A] hover:border-[#0047FF] hover:text-[#0047FF]"
          }`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

function CustomDateRange({ from, to, onChange, testIdPrefix }) {
  return (
    <div className="flex items-end gap-3 flex-wrap">
      <div>
        <label className="text-xs font-medium text-[#525B6A] mb-1 block">From</label>
        <input
          type="date"
          value={from || ""}
          onChange={(e) => onChange({ from: e.target.value, to })}
          data-testid={`${testIdPrefix}-from`}
          className="px-3 py-1.5 border border-[#E2E8F0] rounded-md text-sm"
        />
      </div>
      <div>
        <label className="text-xs font-medium text-[#525B6A] mb-1 block">To</label>
        <input
          type="date"
          value={to || ""}
          onChange={(e) => onChange({ from, to: e.target.value })}
          data-testid={`${testIdPrefix}-to`}
          className="px-3 py-1.5 border border-[#E2E8F0] rounded-md text-sm"
        />
      </div>
    </div>
  );
}

function ActionRow({ onDownload, onEmail, email, setEmail, downloading, emailing, dlDisabled }) {
  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={onDownload}
        disabled={dlDisabled || downloading}
        data-testid="export-download-btn"
        className="btn-primary w-full px-4 py-2.5 rounded-md text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
        {downloading ? "Preparing…" : "Download"}
      </button>
      <div className="text-xs uppercase tracking-wider text-[#8A94A6] font-medium pt-2 border-t border-[#E2E8F0]">Email report</div>
      <div className="flex gap-2">
        <input
          type="email"
          placeholder="you@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="export-email-input"
          className="flex-1 px-3 py-2 border border-[#E2E8F0] rounded-md text-sm"
        />
        <button
          type="button"
          onClick={onEmail}
          disabled={emailing || dlDisabled}
          data-testid="export-email-btn"
          className="btn-ghost px-3 py-2 rounded-md text-sm inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {emailing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
          Send
        </button>
      </div>
      <div className="text-[11px] text-[#8A94A6]">Leave blank to send to your login email.</div>
    </div>
  );
}

export default function ExportModal({ projectId, projectDomain, onClose }) {
  const [tab, setTab] = useState("csv");
  const [range, setRange] = useState({ csv: "today", pdf: "7", txt: "7" });
  const [dates, setDates] = useState({ from: "", to: "" });
  const [sections, setSections] = useState(new Set(PDF_SECTIONS.map((s) => s.key)));
  const [email, setEmail] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [emailing, setEmailing] = useState(false);

  const activeRange = range[tab];
  const isCustom = activeRange === "custom";
  const rangeValid = !isCustom || (dates.from && dates.to && dates.from <= dates.to);
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  const params = () => {
    const p = { range: isCustom ? "custom" : activeRange, tz };
    if (isCustom) { p.from = dates.from; p.to = dates.to; }
    return p;
  };

  const filenameStem = `serpnex-${(projectDomain || "report").replace(/\./g, "-")}`;

  const download = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("sn_token");
      if (tab === "pdf") {
        // PDF uses the ProReport generator — go through /reports/pdf/generate.
        const daysMap = { today: 1, "7": 7, "30": 30 };
        const period = isCustom
          ? { date_range: "custom", start_date: dates.from, end_date: dates.to, days: (new Date(dates.to) - new Date(dates.from)) / 86400000 + 1 }
          : { date_range: activeRange === "today" ? "24h" : `${activeRange}d`, days: daysMap[activeRange] || 7 };
        const resp = await fetch(`${API}/reports/pdf/generate`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            project_id: projectId,
            sections: Array.from(sections),
            branding: {},
            client: {},
            filters: {},
            period,
          }),
        });
        if (!resp.ok) throw new Error("pdf failed");
        const blob = await resp.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${filenameStem}.pdf`;
        a.click();
      } else {
        // CSV / TXT go through the exports router.
        const qs = new URLSearchParams({ project_id: projectId, ...params() }).toString();
        const url = `${API}/exports/${tab}?${qs}`;
        const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          throw new Error(err.detail || `${tab} export failed`);
        }
        const blob = await resp.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${filenameStem}.${tab}`;
        a.click();
      }
      toast.success(`${tab.toUpperCase()} downloaded.`);
    } catch (e) {
      toast.error(e?.message || `${tab.toUpperCase()} download failed.`);
    } finally { setDownloading(false); }
  };

  const emailIt = async () => {
    setEmailing(true);
    try {
      const body = {
        project_id: projectId,
        kind: tab,
        range: isCustom ? "custom" : activeRange,
        tz,
      };
      if (isCustom) { body.from = dates.from; body.to = dates.to; }
      if (email) body.recipient = email;
      if (tab === "pdf") body.pdf_sections = Array.from(sections);
      const r = await api.post("/exports/email", body);
      toast.success(`Emailed to ${r.data.recipient}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Email failed.");
    } finally { setEmailing(false); }
  };

  const setActiveRange = (key) => setRange((prev) => ({ ...prev, [tab]: key }));
  const toggleSection = (key) => setSections((s) => {
    const next = new Set(s);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="export-modal">
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E2E8F0]">
          <div>
            <h2 className="text-lg font-display font-semibold text-[#0A0D14]">Export data</h2>
            <p className="text-xs text-[#8A94A6] mt-0.5">Pick a format, choose the date range, download or email.</p>
          </div>
          <button onClick={onClose} data-testid="export-close" className="text-[#8A94A6] hover:text-[#0A0D14]">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex border-b border-[#E2E8F0]">
          {TABS.map((t) => (
            <TabButton key={t.key} tab={t} active={tab === t.key} onClick={() => setTab(t.key)} />
          ))}
        </div>

        <div className="p-6 overflow-y-auto">
          {tab === "csv" && (
            <div className="space-y-5">
              <div>
                <label className="text-xs font-medium text-[#525B6A] mb-2 block uppercase tracking-wider">Period</label>
                <RangeChips presets={CSV_PRESETS} value={activeRange} onChange={setActiveRange} testIdPrefix="csv" />
                <button
                  type="button"
                  onClick={() => setActiveRange("custom")}
                  data-testid="csv-range-custom"
                  className={`mt-2 inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border ${activeRange === "custom" ? "bg-[#0047FF] border-[#0047FF] text-white" : "bg-white border-[#E2E8F0] text-[#525B6A] hover:border-[#0047FF]"}`}
                >
                  <Calendar className="h-3.5 w-3.5" /> Custom range
                </button>
              </div>
              {isCustom && <CustomDateRange {...dates} onChange={setDates} testIdPrefix="csv" />}
              <ActionRow
                onDownload={download} onEmail={emailIt}
                email={email} setEmail={setEmail}
                downloading={downloading} emailing={emailing}
                dlDisabled={!rangeValid}
              />
            </div>
          )}

          {tab === "pdf" && (
            <div className="space-y-5">
              <div>
                <label className="text-xs font-medium text-[#525B6A] mb-2 block uppercase tracking-wider">Report period</label>
                <select
                  value={activeRange}
                  onChange={(e) => setActiveRange(e.target.value)}
                  data-testid="pdf-range-select"
                  className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white"
                >
                  {PDF_PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
              </div>
              {isCustom && <CustomDateRange {...dates} onChange={setDates} testIdPrefix="pdf" />}
              <div>
                <label className="text-xs font-medium text-[#525B6A] mb-2 block uppercase tracking-wider">Report sections ({sections.size}/{PDF_SECTIONS.length})</label>
                <div className="grid grid-cols-2 gap-2">
                  {PDF_SECTIONS.map((s) => (
                    <label key={s.key} className="flex items-center gap-2 px-3 py-2 border border-[#E2E8F0] rounded-md cursor-pointer hover:border-[#0047FF]">
                      <input
                        type="checkbox"
                        checked={sections.has(s.key)}
                        onChange={() => toggleSection(s.key)}
                        data-testid={`pdf-section-${s.key}`}
                      />
                      <span className="text-sm text-[#0A0D14]">{s.label}</span>
                    </label>
                  ))}
                </div>
                <div className="mt-1.5 text-[11px] text-[#8A94A6]">Cover, KPI overview, keyword table and footer are always included.</div>
              </div>
              <ActionRow
                onDownload={download} onEmail={emailIt}
                email={email} setEmail={setEmail}
                downloading={downloading} emailing={emailing}
                dlDisabled={!rangeValid || sections.size === 0}
              />
            </div>
          )}

          {tab === "txt" && (
            <div className="space-y-5">
              <div>
                <label className="text-xs font-medium text-[#525B6A] mb-2 block uppercase tracking-wider">Period</label>
                <RangeChips presets={TXT_PRESETS} value={activeRange} onChange={setActiveRange} testIdPrefix="txt" />
              </div>
              <ActionRow
                onDownload={download} onEmail={emailIt}
                email={email} setEmail={setEmail}
                downloading={downloading} emailing={emailing}
                dlDisabled={!rangeValid}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
